const STRIPPABLE = new Set(["image/jpeg", "image/png", "image/webp"]);

export async function stripExifIfImage(file: File): Promise<File> {
  if (!STRIPPABLE.has(file.type)) return file;

  try {
    const dataUrl = await readAsDataURL(file);
    const img = await loadImage(dataUrl);

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, file.type, 0.92),
    );
    if (!blob) return file;
    return new File([blob], file.name, { type: file.type });
  } catch {
    return file;
  }
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error ?? new Error("read failed"));
    r.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  // Race the image-load against a short timeout: some environments (notably
  // jsdom) silently never fire onload or onerror for non-decodable sources,
  // and we don't want EXIF stripping to block the upload flow indefinitely.
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error("decode timed out")), 2000);
    img.onload = () => {
      clearTimeout(timer);
      if (img.naturalWidth === 0) {
        reject(new Error("decode failed"));
        return;
      }
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("decode failed"));
    };
    img.src = src;
  });
}
